/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useMergeRefs } from '@floating-ui/react';

import { typography2 } from '@coursera/cds-core';

import TooltipArrow from 'bundles/common/components/Tooltip/TooltipArrow';
import { ARROW_HEIGHT } from 'bundles/common/components/Tooltip/constants';
import type { Placement, Variant } from 'bundles/common/components/Tooltip/types';

const styles = {
  tooltip: css`
    --tooltip-background-color: var(--cds-color-emphasis-primary-background-xxstrong);
    --tooltip-text-color: var(--cds-color-neutral-primary-invert);
    --tooltip-box-shadow: none;

    position: relative;
    z-index: 100001;
    box-sizing: border-box;
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    background-color: var(--tooltip-background-color);
    border-radius: var(--cds-border-radius-50);
    display: inline-block;
    max-width: 200px;
    outline: none;
    box-shadow: var(--tooltip-box-shadow);
  `,
  focusVisible: css`
    &:focus {
      ::after {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border-radius: var(--cds-border-radius-50);
        box-shadow: 0 0 0 1px var(--cds-color-purple-700), 0 0 0 2px var(--cds-color-blue-25);
      }
    }
  `,
  secondaryTooltip: css`
    --tooltip-background-color: var(--cds-color-neutral-background-primary);
    --tooltip-text-color: var(--cds-color-neutral-primary);
    --tooltip-box-shadow: 0 0 8px 0 var(--cds-color-darken-200),
      var(--tooltip-box-shadow-x-offset) var(--tooltip-box-shadow-y-offset) 10px var(--cds-color-darken-200);
  `,
  left: css`
    --tooltip-box-shadow-x-offset: ${ARROW_HEIGHT}px;
    --tooltip-box-shadow-y-offset: 0px;
  `,
  right: css`
    --tooltip-box-shadow-x-offset: calc(${ARROW_HEIGHT}px * -1);
    --tooltip-box-shadow-y-offset: 0px;
  `,
  top: css`
    --tooltip-box-shadow-x-offset: 0px;
    --tooltip-box-shadow-y-offset: ${ARROW_HEIGHT}px;
  `,
  bottom: css`
    --tooltip-box-shadow-x-offset: 0px;
    --tooltip-box-shadow-y-offset: calc(${ARROW_HEIGHT}px * -1);
  `,
  label: css`
    ${typography2.bodySecondary};
    color: var(--tooltip-text-color);
    text-align: center;
    overflow-wrap: break-word;
  `,
};

export type Props = React.HTMLAttributes<HTMLElement> & {
  arrow?: React.ReactNode;
  variant?: Variant;
  placement?: Placement;
  focusVisible?: boolean;
  arrowClassName?: string;
  enableArrow?: boolean;
};

const Tooltip = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    variant = 'primary',
    children,
    arrow,
    placement = 'top',
    focusVisible,
    arrowClassName,
    enableArrow = true,
    ...attributes
  } = props;
  const secondary = variant === 'secondary';
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);
  const mergedRefs = useMergeRefs([ref, setTooltipRef]);

  return (
    <div
      ref={mergedRefs}
      css={[
        styles.tooltip,
        styles[placement],
        secondary && styles.secondaryTooltip,
        focusVisible && styles.focusVisible,
      ]}
      data-variant={variant}
      {...attributes}
    >
      {enableArrow &&
        (arrow || (
          <TooltipArrow
            tooltipElement={tooltipRef}
            placement={placement}
            className={arrowClassName}
            data-testid="tooltip-arrow"
          />
        ))}
      <div css={styles.label}>{children}</div>
    </div>
  );
});

export default Tooltip;
